import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import {
  ArrowRight, Shield, Lock, Server, FileCheck, Route as RouteIcon,
  Users, TrendingUp, Target, HeartPulse, ClipboardList, DollarSign,
  Activity, BarChart3, Zap, Layers, Globe,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import React from 'react';
import { OrganicShapes } from '../components/decor/OrganicShapes';
import { Reveal } from '../components/Reveal';
const dashboardsSvg = new URL('../assets/healthbridge-dashboards.svg', import.meta.url).href;

const moduleAccents = ['#34AFFB', '#70D6C5', '#34AFFB', '#70D6C5', '#34AFFB', '#FF7F66'];

const modules = [
  {
    icon: Users,
    title: 'Attribution Hub',
    description: 'Reconcile member attribution across network, payer, and provider group. Counts that tally, with no double-counting.',
  },
  {
    icon: TrendingUp,
    title: 'StarIQ Intelligence',
    description: 'Measure performance, gaps, and the drivers behind the rating priced continuously against forecasted cut points for Medicare Advantage.',
  },
  {
    icon: Target,
    title: 'AccountableIQ Intelligence',
    description: 'Calculate ACO REACH and MSSP shared-savings for real-time analytics and quality insights.',
  },
  {
    icon: ClipboardList,
    title: 'HEDISIQ Intelligence',
    description: 'Track HEDIS measure calculation and care-gap closure.',
  },
  {
    icon: HeartPulse,
    title: 'Care Management',
    description: 'Dispatch member-level worklists and intervention workflows.',
  },
  {
    icon: DollarSign,
    title: 'Risk Adjustment Tools',
    description: 'Validate MEAT, RADV, HCC coding and RAF across CMS-HCC V28 and HHS-HCC.',
  },
];

const capabilities = [
  {
    icon: Activity,
    title: 'Quality performance',
    description: 'Monitor quality measures and care gaps in time to intervene before performance is finalized.',
  },
  {
    icon: Users,
    title: 'Risk adjustment & attribution',
    description: 'Identify documentation gaps and attribution drift before they translate into revenue loss or downside risk.',
  },
  {
    icon: DollarSign,
    title: 'Financial performance',
    description: 'Track cost trends and shared savings drivers with clinical and operational context intact.',
  },
  {
    icon: BarChart3,
    title: 'Population health',
    description: 'Understand patient panels and risk stratification through a unified clinical and claims lens.',
  },
  {
    icon: Zap,
    title: 'Operational efficiency',
    description: 'Surface operational constraints that directly impact access, throughput, and quality outcomes.',
  },
  {
    icon: Shield,
    title: 'Revenue integrity',
    description: 'Protect reimbursement by aligning coding accuracy, documentation, and performance accountability.',
  },
];

const moats = [
  {
    icon: FileCheck,
    title: 'Built to survive an audit.',
    description: "Every code we accept is backed by the clinical documentation that proves it's real, so it holds up to audits. We're building on accuracy and guarantees.",
  },
  {
    icon: Lock,
    title: 'A complete paper trail.',
    description: "Every action we take is logged and can't be changed after the fact, so there's clear evidence behind every number you report.",
  },
  {
    icon: RouteIcon,
    title: 'Humans make the final call.',
    description: "Our AI never decides on a patient's care on its own. It flags what needs a closer look and hands it to a real clinician to make the call.",
  },
];

const trustColumns = [
  {
    title: 'Ownership',
    description: 'You own the data, pipelines, and license. Everything runs on open-source systems and stays fully portable, no proprietary formats to lock you in.',
  },
  {
    title: 'Deployment',
    description: 'Single-tenant and customer-hosted, air-gapped and Pleroma-managed within your own walls. AI models are self-contained, so nothing ever leaves your environment.',
  },
  {
    title: 'Compliance',
    description: 'HIPAA and SOC compliant, with governed, role-based access built in by design rather than bolted on through configuration.',
  },
];

const governancePoints = [
  {
    icon: Shield,
    title: 'Trust and accountability by design.',
    description: 'Explicit governance, role-based access, and enterprise-grade controls. Trust is established because governance is explicit, not assumed.',
  },
  {
    icon: Layers,
    title: 'Separation without fragmentation.',
    description: 'Secure single-tenant intelligence preserves organizational boundaries while enabling enterprise-wide performance management and visibility without data leakage.',
  },
  {
    icon: Globe,
    title: 'Clinical and financial data, one language.',
    description: 'A shared semantic foundation so clinicians, IT, and finance work from the same numbers.',
  },
];

export function HealthBridgePage() {
  const location = useLocation();

  React.useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      window.requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  }, [location]);

  return (
    <div className="min-h-screen">
      <Header />

      {/* 1. Hero — financial hook */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#f9fbfc] via-white to-[#F4F7F9] py-12 lg:py-16">
        <OrganicShapes variant="hero" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal as="div" direction="right">
              <span className="border-l-4 border-[#34AFFB] pl-3 text-[#34AFFB] text-sm font-semibold inline-block mb-6">
                HealthBridge by PleromaHealth
              </span>
              <h1 className="text-5xl lg:text-6xl text-[#1f2937] mb-4 leading-tight">
                The financial intelligence layer for value-based care
              </h1>
              <p className="text-xl text-[#4B5563] mb-8 leading-relaxed">
                Every open gap connected to its dollar impact. See the dollars, act before the cliff, and survive the audit.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-[#34AFFB] hover:bg-[#34AFFB]/90 text-white">
                  <Link to="/#contact">
                    Book a demo
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </Reveal>
            <Reveal as="div" direction="left" delay={150} className="hidden lg:block">
              <div className="rounded-3xl overflow-hidden shadow-2xl bg-[#F4F7F9]">
                <img
                  src={dashboardsSvg}
                  alt="HealthBridge dashboard grid — attribution, risk adjustment, Star ratings, and AI planner"
                  className="w-full h-auto"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 2. The stakes / cost of the gap — the 2-year cliff timeline */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="ph-stakes-grid mb-16">
            <Reveal as="div" direction="right">
              <h2 className="text-4xl text-[#1f2937] mb-6">The real problem.</h2>
              <p className="text-lg text-[#4B5563] leading-relaxed mb-4">
                Healthcare organizations aren't short on data. They're short on a trusted, unified view they can act on in time. By the time clinical, claims, and operational data is reconciled, the decisions have already been made often on partial or stale insight.
              </p>
              <p className="text-lg text-[#4B5563] leading-relaxed">
                HealthBridge exists to make the team see the cliff coming while there's still time to act, pricing every open gap against a forecast of where the cut points will land, before CMS publishes them.
              </p>
            </Reveal>
            <Reveal as="div" direction="left" delay={150} className="rounded-3xl p-8 text-white text-center shadow-xl" style={{ background: 'linear-gradient(135deg, #FF7F66 0%, #34AFFB 120%)' }}>
              <p className="text-base font-semibold uppercase tracking-widest text-white/80 mb-3">On a single 4.0 Star cliff</p>
              <div className="ph-cliff-figure font-bold mb-3">$48M<sup>*</sup></div>
              <p className="text-lg text-white/90">in quality bonus payment can ride on the outcome</p>
              <p className="text-xs text-white/70 mt-4"><sup>*</sup>Representative / projected figure</p>
            </Reveal>
          </div>

          <p className="text-center text-sm font-semibold uppercase tracking-widest text-[#4B5563]/60 mb-8">
            Medicare Advantage runs on a 3-year lag — measurement year to payout year
          </p>

          {/* Timeline — connecting line drawn via .ph-timeline::before in globals.css */}
          <div className="ph-timeline">
            {[
                {
                  dot: '#34AFFB',
                  when: 'Measurement year',
                  title: 'Care & data collection',
                  desc: 'HEDIS, CAHPS, HOS, and operational data are collected all year — the only window left to change the outcome.',
                },
                {
                  dot: '#70D6C5',
                  when: '+1 year · October',
                  title: 'Star Rating published',
                  desc: 'CMS sets cut points from that data and publishes the rating on Medicare Plan Finder ahead of Open Enrollment.',
                },
                {
                  dot: '#94A3B8',
                  when: '+2 years · June',
                  title: 'Bid submission',
                  desc: 'Plans lock the published rating into the financial bid submitted to CMS for the following contract year.',
                },
                {
                  dot: '#4B5563',
                  when: '+3 years',
                  title: 'Quality bonus paid',
                  desc: 'Plans at 4.0 Stars or higher receive the benchmark bonus and higher rebates as monthly CMS payments all year.',
                },
              ].map((stage, index) => (
                <Reveal as="div" key={stage.title} delay={index * 150} className="ph-timeline-item">
                  <div className="ph-timeline-dot" style={{ background: stage.dot }}>
                    <span className="ph-timeline-pip" />
                  </div>
                  <div className="ph-timeline-body">
                    <p className="text-xs font-semibold uppercase tracking-widest text-[#4B5563]/60 mb-1">{stage.when}</p>
                    <h3 className="ph-timeline-title text-lg font-semibold text-[#1f2937] mb-1">
                      {stage.title}
                    </h3>
                    <p className="text-[#4B5563] text-sm leading-relaxed">{stage.desc}</p>
                  </div>
                </Reveal>
              ))}
          </div>
        </div>
      </section>

      {/* 3. How it works */}
      <section className="py-20 bg-[#F4F7F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="text-4xl text-[#1f2937] mb-4">How it works</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                step: '01', color: 'border-[#34AFFB]', numColor: 'text-[#34AFFB]',
                title: 'Connect.',
                desc: 'Securely link clinical, claims, and payer data across the healthcare ecosystem to eliminate data silos.',
              },
              {
                step: '02', color: 'border-[#70D6C5]', numColor: 'text-[#70D6C5]',
                title: 'Unify.',
                desc: 'Normalize, deduplicate, and govern data into healthcare-native models aligned to clinical, financial, and regulatory standards.',
              },
              {
                step: '03', color: 'border-[#FF7F66]', numColor: 'text-[#FF7F66]',
                title: 'Act.',
                desc: 'Deliver decision-ready intelligence that enables earlier intervention, protects revenue, and improves outcomes before performance degrades.',
              },
            ].map((item, index) => (
              <Reveal as="div" key={item.step} delay={index * 130} className={`bg-white rounded-2xl p-6 border-l-4 ${item.color}`}>
                <div className="flex items-start gap-4">
                  <span className={`text-2xl font-bold ${item.numColor} flex-shrink-0`}>{item.step}</span>
                  <div>
                    <h4 className="font-semibold text-[#1f2937] mb-1">{item.title}</h4>
                    <p className="text-[#4B5563]">{item.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 3b. Decision intelligence across the risk lifecycle */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-12">
            <h2 className="text-4xl text-[#1f2937] mb-4">Decision intelligence across the risk lifecycle</h2>
            <p className="text-lg text-[#4B5563] leading-relaxed">
            HealthBridge supports the decisions healthcare organizations must make across quality, financial risk, and operational performance without fragmenting insight across tools.
          </p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((c, index) => {
              const Icon = c.icon;
              return (
                <Reveal as="div" key={c.title} delay={(index % 3) * 120} className="bg-[#f9fbfc] rounded-2xl p-6 border border-[#4B5563]/10">
                  <div className="h-12 w-12 bg-[#34AFFB]/10 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="h-6 w-6 text-[#34AFFB]" />
                  </div>
                  <h3 className="font-medium text-[#1f2937] mb-2">{c.title}</h3>
                  <p className="text-[#4B5563] text-sm">{c.description}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. The six modules */}
      <section className="py-20 bg-[#F4F7F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="text-4xl text-[#1f2937] mb-4">Six modules. One platform.</h2>
            
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((m, index) => {
              const Icon = m.icon;
              const accent = moduleAccents[index];
              return (
                <Reveal
                  as="div"
                  key={index}
                  delay={(index % 3) * 120}
                  className="group bg-white rounded-2xl p-6 border border-[#4B5563]/10 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden"
                >
                  <span className="absolute top-0 left-0 h-1 w-full" style={{ background: accent }} />
                  <div className="h-10 w-10 rounded-lg flex items-center justify-center mb-4" style={{ background: `${accent}1a` }}>
                    <Icon className="h-5 w-5" style={{ color: accent }} />
                  </div>
                  <h3 className="font-semibold text-[#1f2937] mb-2">{m.title}</h3>
                  <p className="text-[#4B5563] text-sm">{m.description}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5. Interoperability included */}
      <section className="py-16 bg-white">
        <Reveal className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl text-[#1f2937] mb-4">Interoperability isn't an add-on.</h2>
          <p className="text-lg text-[#4B5563] leading-relaxed">
            200+ connectors and HL7 v2-to-FHIR R5 transformation come standard, not as a module you have to unlock later. Most platforms make you buy a separate interface engine or quietly tack on an ingestion fee. We just built it in, so you have one less vendor to manage.
          </p>
        </Reveal>
      </section>

      {/* 6. Defensibility is the moat */}
      <section className="py-20 bg-[#F4F7F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <h2 className="text-4xl text-[#1f2937] mb-4">Codes that hold up under audit.</h2>
            <p className="text-xl text-[#4B5563] max-w-2xl mx-auto">
              Everyone else helps you find more codes. Almost no one makes sure those codes survive when CMS checks your work. Finding codes is crowded, proving they'll stand up is where the real value is.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8">
            {moats.map((p, index) => {
              const Icon = p.icon;
              return (
                <Reveal as="div" key={index} delay={index * 130} className="bg-[#f9fbfc] rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
                  <div className="h-12 w-12 bg-[#34AFFB]/10 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="h-6 w-6 text-[#34AFFB]" />
                  </div>
                  <h3 className="font-medium text-[#1f2937] mb-3">{p.title}</h3>
                  <p className="text-[#4B5563]">{p.description}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 7. Trust & Security — dark band, aligns with the HomePage TrustStrip */}
      <section id="trust" className="py-20 bg-[#1f2937]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <span className="border-l-4 border-[#34AFFB] pl-3 text-[#34AFFB] text-sm font-semibold">
              Trust &amp; Security
            </span>
            <h2 className="mt-6 text-4xl text-white">Built for healthcare risk</h2>
            <p className="text-lg leading-relaxed" style={{ color: 'rgba(244,247,249,0.65)' }}>
              Three design principles that make HealthBridge enterprise-grade by construction, not by configuration.
            </p>
          </Reveal>
          {/* Subsection A — the three design principles */}
          <div className="grid md:grid-cols-3 gap-8">
            {governancePoints.map((p, index) => {
              const Icon = p.icon;
              return (
                <Reveal as="div" key={p.title} delay={index * 130} className="rounded-2xl p-8 border" style={{ background: '#273240', borderColor: 'rgba(255,255,255,0.08)' }}>
                  <div className="h-12 w-12 rounded-xl flex items-center justify-center mb-8" style={{ background: 'rgba(52,175,251,0.15)' }}>
                    <Icon className="h-6 w-6 text-[#34AFFB]" />
                  </div>
                  <h3 className="font-medium mb-2" style={{ color: '#F4F7F9' }}>{p.title}</h3>
                  <p className="text-sm" style={{ color: 'rgba(244,247,249,0.6)' }}>{p.description}</p>
                </Reveal>
              );
            })}
          </div>

          {/* Subsection B — ownership, deployment & compliance (distinct panel) */}
          <Reveal as="div" delay={120} className="mt-16 rounded-3xl border p-8" style={{ background: 'rgba(0,0,0,0.2)', borderColor: 'rgba(255,255,255,0.06)' }}>
            <div className="text-center mb-8">
              <h3 className="text-2xl mb-2" style={{ color: '#F4F7F9' }}>Ownership, deployment &amp; compliance</h3>
              <p style={{ color: 'rgba(244,247,249,0.6)' }}>The controls and guarantees you keep, inside your own environment.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {trustColumns.map((col) => (
                <div key={col.title} className="rounded-2xl p-6 border" style={{ background: '#273240', borderColor: 'rgba(255,255,255,0.08)' }}>
                  <div className="h-11 w-11 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(52,175,251,0.15)' }}>
                    {col.title === 'Ownership' && <Shield className="h-5 w-5 text-[#34AFFB]" />}
                    {col.title === 'Deployment' && <Server className="h-5 w-5 text-[#34AFFB]" />}
                    {col.title === 'Compliance' && <FileCheck className="h-5 w-5 text-[#34AFFB]" />}
                  </div>
                  <h4 className="text-lg font-medium mb-2" style={{ color: '#F4F7F9' }}>{col.title}</h4>
                  <p className="text-sm" style={{ color: 'rgba(244,247,249,0.6)' }}>{col.description}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 8. Outcomes we model */}
      <section className="py-20 bg-[#F4F7F9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-4">
            <h2 className="text-4xl text-[#1f2937] mb-4">Designed to deliver</h2>
            <p className="text-sm text-[#4B5563] mb-12">
              Representative / projected figures based on platform architecture and published industry benchmarks.
            </p>
          </Reveal>
          <div className="ph-metrics-5">
            {[
              { stat: 'Up to 25%', label: 'Reduction in care-gap closure time' },
              { stat: 'Up to 15%', label: 'Improvement in HCC recapture' },
              { stat: 'Up to 40%', label: 'Faster time-to-insight vs. legacy reporting' },
              { stat: '$12,614', label: 'Revenue recaptured per high-risk member' },
              { stat: '< 24 hrs', label: 'From data captured to insight delivered' },
            ].map((item, index) => (
              <Reveal as="div" key={index} direction="scale" delay={index * 90} className="bg-white rounded-2xl p-6 text-center shadow-sm border border-[#4B5563]/10">
                <div className="text-2xl font-bold text-[#34AFFB] mb-3">{item.stat}</div>
                <p className="text-[#4B5563] text-sm">{item.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 9. You own it (Services-as-Software, woven) */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal as="div" direction="scale" className="rounded-3xl text-white flex flex-col items-center text-center" style={{ background: 'linear-gradient(to right, #34AFFB, #70D6C5)', padding: '48px 40px' }}>
            <p className="font-semibold text-sm uppercase tracking-wider mb-5" style={{ color: 'rgba(255,255,255,0.75)' }}>Services-as-Software in practice</p>
            <h2 className="text-3xl text-white mb-4 max-w-2xl">You own it.</h2>
            <p className="text-lg mb-8 max-w-2xl" style={{ color: 'rgba(255,255,255,0.88)' }}>
              Client-owned, no PMPM-forever, no lock-in. HealthBridge is built inside your own environment you own the software, the pipelines, the lineage, and the IP.
            </p>
            <Button asChild size="lg" className="bg-white hover:bg-white/90 text-[#34AFFB]">
              <Link to="/about#how-we-deliver">
                Learn about our delivery model
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </Reveal>
        </div>
      </section>

      {/* 10. CTA */}
      <section className="relative overflow-hidden py-20" style={{ background: 'linear-gradient(to right, #34AFFB, #70D6C5)' }}>
        <OrganicShapes variant="band" />
        <Reveal className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl text-white mb-4">See what's going wrong while there's still time to fix it.</h2>
          <Button asChild size="lg" className="bg-white text-[#34AFFB] hover:bg-white/90">
            <Link to="/#contact">
              Book a demo
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}
